import React, { useMemo, useCallback } from 'react';
import { Box, Card, CardContent, IconButton, Paper, Button } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { getCardSx } from '../../theme/tokens';
import { updateUnitContent, uploadUnitAsset, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm5_u5_dali';

const images = [
  { key: 'dali_symbol', src: '/assets/drawable/dali_symbol.png', label: 'DALI Symbol' }
];

const textBlocks = [
    { key: 'dali_intro', heading: 'Was ist DALI?', body: 'DALI (Digital Addressable Lighting Interface) ist ein standardisiertes, bidirektionales digitales Kommunikationsprotokoll (IEC 62386), das speziell für die Steuerung von elektronischen Vorschaltgeräten (EVGs) für Leuchtmittel, LED-Treibern und anderen Beleuchtungskomponenten entwickelt wurde. Es ermöglicht die individuelle Adressierung und digitale Steuerung jeder einzelnen Leuchte und bietet somit eine hohe Flexibilität und Kontrolle über die Beleuchtung in Gebäuden.' },
    { key: 'dali_features', heading: '', body: 'DALI-Systeme ermöglichen nicht nur das einfache Ein- und Ausschalten sowie das stufenlose Dimmen von Leuchten, sondern auch die Konfiguration von Geräteparametern, die Abfrage von Geräteinformationen (z.B. Fehlerstatus, Energieverbrauch) und die zentrale Steuerung von Lichtszenen und -gruppen.' },
    { key: 'dali_technical', heading: 'Technische Details und Funktionsweise', body: 'Die physikalische Schicht von DALI basiert auf einer Zweidraht-Leitung, die sowohl zur Datenübertragung als auch zur Stromversorgung der DALI-Geräte dient (Busspannung typischerweise 16V DC). Die Datenübertragung erfolgt digital mit einer relativ geringen Datenrate.' },
    { key: 'dali_components', heading: 'Wichtige DALI-Komponenten', body: '- DALI-Controller (Master): Steuert den DALI-Bus, sendet Befehle und empfängt Antworten.\n- Elektronische Vorschaltgeräte (EVGs) / LED-Treiber (Slaves): Enthalten die DALI-Schnittstelle und steuern die Leuchtmittel.\n- Steuergeräte (Input Devices): Senden DALI-Befehle (z.B. Taster, Drehregler, Bewegungsmelder).' },
    { key: 'dali_advantages', heading: 'Vorteile von DALI', body: '- Digitale Steuerung: Präzise und stabile Steuerung der Beleuchtung.\n- Individuelle Adressierung: Jede Leuchte kann einzeln gesteuert und konfiguriert werden.\n- Gruppenbildung: Ermöglicht die gemeinsame Steuerung mehrerer Leuchten.\n- Bidirektionale Kommunikation: Controller kann Informationen von den Geräten abfragen.' }
];

const M5_U5_Dali: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM5, canUploadImagesM5 } = useAuth();
  const canEditTexts = canEditTextsM5;
  const canUploadImages = canUploadImagesM5;
  const { darkMode } = useTheme();

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = useMemo(() => textBlocks.map(tb => ({ ...tb, body: override?.texts?.[tb.key] || tb.body })), [override]);
  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };
  const fullText = useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = useCallback(async (blockKey: string, newBody: string) => {
    if (!token) return alert('Kein Token.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try { await updateUnitContent(PAGE_ID, { texts: { [blockKey]: newBody } }, token); alert('Gespeichert.'); await refresh(); } catch (e) { console.error(e); alert('Fehler.'); }
  }, [token, refresh, canEditTexts]);

  const handleImageReplace = useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try { await uploadUnitAsset(PAGE_ID, imageKey, file, token); alert('Bild aktualisiert.'); await refresh(); } catch (e) { console.error(e); alert('Fehler.'); }
  }, [token, refresh, canUploadImages]);

  const ttsBtn = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Text vorlesen', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '5.5 DALI', actions: ttsBtn },
    React.createElement(Paper, { elevation: 0, sx: { backgroundColor: 'transparent' } },
      React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 3 } },
        React.createElement(Box, null,
          React.createElement(ZoomableImage, {
            src: imgSrc(images[0].key, images[0].src),
            label: images[0].label,
            onReplace: canUploadImages ? (f: File) => handleImageReplace(images[0].key, f) : undefined
          })
        ),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 3 } },
          mergedBlocks.map(blk => React.createElement(Card, { key: blk.key, elevation: 2, sx: getCardSx(darkMode) },
            React.createElement(CardContent, null,
              React.createElement(EditableTextBlock, { pageId: PAGE_ID, blockId: blk.key, heading: blk.heading, body: blk.body, onSave: canEditTexts ? (newBody: string) => handleTextSave(blk.key, newBody) : undefined })
            )
          ))
        )
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
      React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m5_u99_quiz') }, 'Zum Quiz')
    )
  );
};

export default M5_U5_Dali;

